import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import axiosInstance from '../utils/axiosInstance';
import config from '../utils/config';
import { useAuth } from '../context/AuthContext';
import { formatTimestamp } from '../utils/formatTimestamp';
import { EVALUATOR_PARAM_DOCS } from '../utils/healthEvaluatorDocs';
import Button from '../components/ui/Button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/pilot-ui/tabs';
import { ToggleGroup, ToggleGroupItem } from '../components/pilot-ui/toggle-group';
import { Switch } from '../components/pilot-ui/switch';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/pilot-ui/collapsible';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/pilot-ui/tooltip';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/pilot-ui/select';
import { Input } from '../components/pilot-ui/input';
import { Textarea } from '../components/pilot-ui/textarea';
import { Label } from '../components/pilot-ui/label';

// Plan 98 — Fleet Health ranking + device panel + evaluators admin.
// Labels, weights, and exclusions affect the next run's scores.

type ScoreKey = 'sum' | 'category_max' | 'capped' | 'correlation';
const SCORE_KEYS: ScoreKey[] = ['sum', 'category_max', 'capped', 'correlation'];
const isScoreKey = (value: string): value is ScoreKey => SCORE_KEYS.some(key => key === value);
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

interface Freshness {
  latest_published_date: string | null;
  latest_published_finished_at: string | null;
  scoring_hash: string | null;
  previous_published_date: string | null;
  previous_scoring_hash: string | null;
  week_ago_published_date: string | null;
  week_ago_scoring_hash: string | null;
  latest_run_status: string | null;
  latest_run_date: string | null;
}

interface BreakdownEntry {
  finding_id: number | null;
  evaluator_key: string;
  problem_type: string;
  category: string;
  status: string;
  contribution: number;
  suppressed: boolean;
  enabled: boolean;
}

interface ScoreRow {
  device_id: number;
  device_name: string;
  entity_path: string;
  scores: Record<ScoreKey, number>;
  breakdown: BreakdownEntry[];
  scoring_hash: string;
  finding_count: number;
  as_of_date: string;
  input_freshness: { daily_calcs: boolean; alerts: boolean } | null;
}

interface Finding {
  id: number;
  evaluator_key: string;
  problem_type: string;
  category: string;
  severity: number;
  confidence: number;
  status: string;
  first_seen: string;
  last_seen: string;
  resolved_at: string | null;
  resolved_by: string | null;
  summary: string;
  evidence: Record<string, unknown>;
  suppressed: boolean;
  evaluator_enabled: boolean;
  effective_verdict: string | null;
}

interface DevicePanelData {
  device: { id: number; name: string };
  findings: Finding[];
  score_history: { as_of_date: string; scores: Record<ScoreKey, number>; scoring_hash: string }[];
  exclusion: { id: number; reason: string; source: string } | null;
  freshness: Freshness;
}

interface EvaluatorRow {
  evaluator_key: string;
  kind: string;
  tier: string;
  problem_types: string[];
  requires: string[];
  weight: number;
  enabled: boolean;
  version: string;
  config: Record<string, unknown>;
  resolve_after_misses: number;
  updated_at: string;
  description?: string | null;
  last_run: { ok: string; errors: string; skipped: string; findings_emitted: string } | null;
}

interface Exclusion {
  id: number;
  device_id: number;
  device_name: string;
  reason: string;
  source: string;
  active: boolean;
  created_at: string;
}

type EvaluatorEdit = { weight: string; config: string; resolve_after_misses: string };
type EvaluatorField = 'weight' | 'resolve_after_misses' | 'config';
type EvaluatorFieldError = { key: string; field: EvaluatorField; message: string };

const canonicalJson = (value: unknown): string => JSON.stringify(value, (_key, item) =>
  isRecord(item)
    ? Object.fromEntries(Object.entries(item).sort(([left], [right]) => left.localeCompare(right)))
    : item) ?? '';

const validateEvaluatorEdit = (edit: EvaluatorEdit): { field: EvaluatorField; message: string } | null => {
  if (edit.weight.trim() && (!Number.isFinite(Number(edit.weight)) || Number(edit.weight) < 0)) {
    return { field: 'weight', message: 'Weight must be a nonnegative number.' };
  }
  if (edit.resolve_after_misses.trim() && (!Number.isInteger(Number(edit.resolve_after_misses)) || Number(edit.resolve_after_misses) < 1)) {
    return { field: 'resolve_after_misses', message: 'Resolve after misses must be a whole number of at least one.' };
  }
  try {
    if (!isRecord(JSON.parse(edit.config))) {
      return { field: 'config', message: 'Settings must be a JSON object.' };
    }
  } catch {
    return { field: 'config', message: 'Settings must be valid JSON.' };
  }
  return null;
};

const evaluatorHasChanges = (ev: EvaluatorRow, edit: EvaluatorEdit | undefined): boolean => {
  if (!edit) return false;
  if (edit.weight.trim() && Number(edit.weight) !== ev.weight) return true;
  if (edit.resolve_after_misses.trim() && Number(edit.resolve_after_misses) !== ev.resolve_after_misses) return true;
  try {
    return canonicalJson(JSON.parse(edit.config)) !== canonicalJson(ev.config);
  } catch {
    return true;
  }
};

const CATEGORY_COLORS: Record<string, string> = {
  comms: 'bg-purple-100 text-purple-800 border-purple-300',
  sensor: 'border-status-error-border bg-status-error text-status-error-foreground',
  clustering: 'border-status-warning-border bg-status-warning text-status-warning-foreground',
  data_integrity: 'border-status-info-border bg-status-info text-status-info-foreground',
};
const chipClass = (cat: string) =>
  `inline-block border rounded-sm px-1.5 py-0.5 text-xs mr-1 ${CATEGORY_COLORS[cat] || 'border-status-neutral-border bg-status-neutral text-status-neutral-foreground'}`;

const VERDICTS = ['confirmed', 'false_positive', 'fixed', 'wont_fix'] as const;

const daysAgo = (iso: string | null): number | null => {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
};

// Sparkline with vertical break markers where scoring_hash changes —
// a config change must never render as a fake trend.
const ScoreSparkline: React.FC<{
  history: DevicePanelData['score_history']; scoreKey: ScoreKey;
}> = ({ history, scoreKey }) => {
  if (history.length < 2) return <span className="text-xs text-muted-foreground">not enough history</span>;
  const w = 220, h = 40, pad = 2;
  const values = history.map(p => p.scores[scoreKey] ?? 0);
  const max = Math.max(...values, 0.001);
  const x = (i: number) => pad + (i * (w - 2 * pad)) / (history.length - 1);
  const y = (v: number) => h - pad - (v / max) * (h - 2 * pad);
  const points = values.map((v, i) => `${x(i)},${y(v)}`).join(' ');
  const breaks = history
    .map((p, i) => (i > 0 && p.scoring_hash !== history[i - 1].scoring_hash ? i : -1))
    .filter(i => i > 0);
  return (
    <svg width={w} height={h} className="block">
      <polyline points={points} fill="none" stroke="#6366f1" strokeWidth="1.5" />
      {breaks.map(i => (
        <line key={i} x1={x(i)} y1={0} x2={x(i)} y2={h}
              stroke="#f59e0b" strokeWidth="1" strokeDasharray="3,2" />
      ))}
    </svg>
  );
};

const Badge: React.FC<{ label: string; explain: string; color: string }> =
  ({ label, explain, color }) => {
    const [open, setOpen] = useState(false);
    return (
      <TooltipProvider>
        <Tooltip open={open} onOpenChange={setOpen}>
          <TooltipTrigger
            title={`${label}: ${explain}`}
            className={`cursor-help inline-block border border-control-border rounded-sm px-1.5 py-0.5 text-xs mr-1 ${color}`}
            onFocus={() => setOpen(true)}
            onClick={() => setOpen(true)}
            // A press opens this help (touch has no hover), so the trigger must not also close it.
            closeOnClick={false}
          >
            {label}
          </TooltipTrigger>
          <TooltipContent>{explain}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

const FleetHealthPage: React.FC = () => {
  const { permissions } = useAuth();
  const canManage = permissions?.includes('manage_health') ?? false;
  const canReadNotes = permissions?.includes('read_notes') ?? false;

  const [tab, setTab] = useState<'ranking' | 'evaluators'>('ranking');
  const [scoreKey, setScoreKey] = useState<ScoreKey>('sum');
  const [rows, setRows] = useState<ScoreRow[]>([]);
  const [prevRows, setPrevRows] = useState<Record<number, number>>({});
  const [prevComparable, setPrevComparable] = useState(false);
  const [trendLabel, setTrendLabel] = useState('7d');
  const [freshness, setFreshness] = useState<Freshness | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string[]>([]);
  const [findClip, setFindClip] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [panel, setPanel] = useState<DevicePanelData | null>(null);
  const [panelLoading, setPanelLoading] = useState(false);
  const [labelNote, setLabelNote] = useState('');

  const [exclusions, setExclusions] = useState<Exclusion[]>([]);
  const [showExcluded, setShowExcluded] = useState(false);
  const exclusionRows = useRef(new Map<number, HTMLLIElement>());
  const restoreExclusionFocus = useRef(false);
  const excludedTriggerRef = useRef<HTMLButtonElement>(null);
  const rankingTabRef = useRef<HTMLButtonElement>(null);

  const [evaluators, setEvaluators] = useState<EvaluatorRow[]>([]);
  const [evalEdit, setEvalEdit] = useState<Record<string, EvaluatorEdit | undefined>>({});
  const [evalError, setEvalError] = useState<string | null>(null);
  const [evalFieldError, setEvalFieldError] = useState<EvaluatorFieldError | null>(null);
  const [newExclusion, setNewExclusion] = useState({ device_id: '', reason: '' });

  const base = config.devicesApiUrl;

  const fetchScores = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ score_key: scoreKey, limit: '200' });
      if (categoryFilter.length) params.set('category', categoryFilter.join(','));
      const r = await axiosInstance.get(`${base}/health/scores?${params}`);
      setRows(r.data.results);
      setFreshness(r.data.freshness);

      // Trend baseline: a run ~7 days back when one exists (honest "7d"
      // column); otherwise the immediately-previous run, relabeled so the
      // heading never overstates the window. Comparable only within one
      // scoring_hash.
      const f: Freshness = r.data.freshness;
      let baselineDate: string | null = null;
      if (f.week_ago_published_date && f.week_ago_scoring_hash === f.scoring_hash) {
        baselineDate = f.week_ago_published_date;
        setTrendLabel('7d');
      } else if (f.previous_published_date && f.previous_scoring_hash === f.scoring_hash) {
        baselineDate = f.previous_published_date;
        setTrendLabel('Δ prev');
      }
      if (baselineDate) {
        const pd = new Date(baselineDate).toISOString().slice(0, 10);
        const pr = await axiosInstance.get(
          `${base}/health/scores?date=${pd}&score_key=${scoreKey}&limit=200`);
        const map: Record<number, number> = {};
        pr.data.results.forEach((row: ScoreRow) => {
          map[row.device_id] = row.scores[scoreKey] ?? 0;
        });
        setPrevRows(map);
        setPrevComparable(true);
      } else {
        setPrevRows({});
        setPrevComparable(false);
      }
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load health scores');
    } finally {
      setLoading(false);
    }
  }, [base, scoreKey, categoryFilter]);

  const fetchExclusions = useCallback(async () => {
    try {
      const r = await axiosInstance.get(`${base}/health/exclusions?active=true`);
      const nextExclusions: Exclusion[] = r.data.results;
      const nextIds = new Set(nextExclusions.map(exclusion => exclusion.id));
      const focusedElement = document.activeElement;
      if (focusedElement && [...exclusionRows.current].some(([id, row]) =>
        !nextIds.has(id) && row.contains(focusedElement))) {
        restoreExclusionFocus.current = true;
      }
      setExclusions(nextExclusions);
    } catch {
      setError('Failed to load excluded devices. Refresh to try again.');
    }
  }, [base]);

  const fetchEvaluators = useCallback(async () => {
    try {
      const r = await axiosInstance.get(`${base}/health/evaluators`);
      setEvaluators(r.data.evaluators);
    } catch (e: any) {
      setEvalError(e?.response?.data?.message || 'Failed to load evaluators');
    }
  }, [base]);

  useEffect(() => { fetchScores(); }, [fetchScores]);
  useEffect(() => { fetchExclusions(); }, [fetchExclusions]);
  useEffect(() => { if (tab === 'evaluators') fetchEvaluators(); }, [tab, fetchEvaluators]);
  useLayoutEffect(() => {
    const shouldRestoreFocus = restoreExclusionFocus.current;
    restoreExclusionFocus.current = false;
    if (!shouldRestoreFocus || document.activeElement !== document.body) return;
    (excludedTriggerRef.current ?? rankingTabRef.current)?.focus();
  }, [exclusions]);

  const openPanel = async (deviceId: number) => {
    if (expandedId === deviceId) { setExpandedId(null); setPanel(null); return; }
    setExpandedId(deviceId);
    setPanel(null);
    setPanelLoading(true);
    try {
      const r = await axiosInstance.get(`${base}/health/devices/${deviceId}`);
      setPanel(r.data);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load device health');
      setExpandedId(null);
    } finally {
      setPanelLoading(false);
    }
  };

  const applyLabel = async (findingId: number, verdict: string) => {
    try {
      await axiosInstance.post(`${base}/health/findings/${findingId}/labels`,
        { verdict, note: labelNote || undefined });
      setLabelNote('');
      if (expandedId) {
        const r = await axiosInstance.get(`${base}/health/devices/${expandedId}`);
        setPanel(r.data);
      }
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Label failed');
    }
  };

  const saveEvaluator = async (ev: EvaluatorRow) => {
    setEvalError(null);
    const edit = evalEdit[ev.evaluator_key];
    if (!evaluatorHasChanges(ev, edit)) return;
    if (!edit) return;
    const validation = validateEvaluatorEdit(edit);
    if (validation) {
      setEvalFieldError({ key: ev.evaluator_key, ...validation });
      return;
    }
    setEvalFieldError(null);
    try {
      const body: { expected_updated_at: string; weight?: number; resolve_after_misses?: number; config?: Record<string, unknown> } = {
        expected_updated_at: new Date(ev.updated_at).toISOString(),
      };
      if (edit?.weight.trim() && Number(edit.weight) !== ev.weight) body.weight = Number(edit.weight);
      if (edit?.resolve_after_misses.trim() && Number(edit.resolve_after_misses) !== ev.resolve_after_misses) body.resolve_after_misses = Number(edit.resolve_after_misses);
      if (edit?.config) {
        const parsed: unknown = JSON.parse(edit.config);
        if (!isRecord(parsed)) throw new Error('Settings must be a JSON object.');
        if (canonicalJson(parsed) !== canonicalJson(ev.config)) body.config = parsed;
      }
      await axiosInstance.patch(`${base}/health/evaluators/${ev.evaluator_key}`, body);
      setEvalEdit(prev => ({ ...prev, [ev.evaluator_key]: undefined }));
      fetchEvaluators();
    } catch (e: any) {
      setEvalError(e?.response?.status === 409
        ? 'Evaluator changed since load — refreshed; re-apply your edit.'
        : (e?.response?.data?.message || (e instanceof Error ? e.message : 'Save failed')));
      if (e?.response?.status === 409) fetchEvaluators();
    }
  };

  const updateEvaluatorEdit = (key: string, edit: EvaluatorEdit) => {
    setEvalEdit(previous => ({ ...previous, [key]: edit }));
    setEvalFieldError(null);
  };

  const toggleEvaluator = async (ev: EvaluatorRow) => {
    setEvalError(null);
    try {
      await axiosInstance.patch(`${base}/health/evaluators/${ev.evaluator_key}`, {
        expected_updated_at: new Date(ev.updated_at).toISOString(),
        enabled: !ev.enabled,
      });
      fetchEvaluators();
    } catch (e: any) {
      setEvalError(e?.response?.data?.message || 'Toggle failed');
      fetchEvaluators();
    }
  };

  const addExclusion = async () => {
    try {
      await axiosInstance.post(`${base}/health/exclusions`, {
        device_id: parseInt(newExclusion.device_id, 10),
        reason: newExclusion.reason,
      });
      setNewExclusion({ device_id: '', reason: '' });
      fetchExclusions();
    } catch (e: any) {
      setEvalError(e?.response?.data?.message || 'Exclusion failed');
    }
  };

  const endExclusion = async (id: number) => {
    setError(null);
    try {
      await axiosInstance.patch(`${base}/health/exclusions/${id}`, { active: false });
      await fetchExclusions();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to end exclusion');
    }
  };

  const allCategories = useMemo(() => {
    const cats = new Set<string>();
    rows.forEach(r => r.breakdown?.forEach(b => { if (b.status === 'open') cats.add(b.category); }));
    return [...cats].sort();
  }, [rows]);

  const excludedIds = useMemo(() => new Set(exclusions.map(x => x.device_id)), [exclusions]);
  const clipQuery = findClip.trim();
  const visibleRows = rows.filter(row => {
    if (excludedIds.has(row.device_id)) return false;
    if (!clipQuery) return true;
    if (/^\d+$/.test(clipQuery)) return row.device_id === Number(clipQuery);
    return row.device_name.toLowerCase().includes(clipQuery.toLowerCase());
  });

  const staleDays = daysAgo(freshness?.latest_published_date ?? null);
  const fleetStale = (staleDays !== null && staleDays > 3)
    || freshness?.latest_run_status === 'completed_degraded';

  const trendFor = (row: ScoreRow): React.ReactNode => {
    if (!prevComparable) {
      return (
        <Badge label="⋯" color="border-status-warning-border bg-status-warning text-status-warning-foreground"
               explain="No comparable previous run: the scoring configuration changed between runs (or there is no earlier published run), so a trend arrow would be misleading." />
      );
    }
    const prev = prevRows[row.device_id];
    if (prev === undefined) return <span className="text-muted-foreground">–</span>;
    const cur = row.scores[scoreKey] ?? 0;
    const delta = cur - prev;
    if (Math.abs(delta) < 0.01) return <span className="text-muted-foreground">→</span>;
    return delta > 0
      ? <span className="text-status-error-foreground">▲ {delta.toFixed(2)}</span>
      : <span className="text-status-success-foreground">▼ {Math.abs(delta).toFixed(2)}</span>;
  };

  return (
    <Tabs value={tab} onValueChange={value => {
      if (value === 'ranking' || value === 'evaluators') {
        setTab(value);
      }
    }} className="p-6 max-w-6xl">
      <div className="flex items-baseline justify-between mb-1">
        <h1 className="text-2xl font-bold">Fleet Health</h1>
        <TabsList aria-label="Fleet health views">
          <TabsTrigger ref={rankingTabRef} value="ranking" title="Show devices ranked by health score, worst first, with their open findings.">Ranking</TabsTrigger>
          <TabsTrigger value="evaluators" title="Show the health evaluators, their settings, and excluded devices.">Evaluators</TabsTrigger>
        </TabsList>
      </div>
      <p className="text-sm text-muted-foreground mb-3">
        {freshness === null
          ? (error ? 'Published run information unavailable.' : 'Loading published run…')
          : freshness.latest_published_date
            ? <>Latest published scores cover {freshness.latest_published_date.slice(0, 10)}.{' '}
                {freshness.latest_published_finished_at
                  ? `Run finished ${formatTimestamp(freshness.latest_published_finished_at)}.`
                  : 'Run finish time unavailable.'}</>
            : 'No run has published yet.'}{' '}
        Labels and config changes take effect on the next run.
      </p>

      {fleetStale && freshness && (
        <div className="border border-status-warning-border bg-amber-50 text-amber-900 rounded-sm p-3 mb-4 text-sm">
          <strong>Health evaluation is stale.</strong>{' '}
          Latest published scores are for {String(freshness.latest_published_date).slice(0, 10)}
          {freshness.latest_run_status === 'completed_degraded' &&
            ' — the most recent run finished degraded (too few devices had fresh data) and was not published'}.
          The ranking below reflects the last good run.
        </div>
      )}
      {error && <div role="alert" className="border border-status-error-border bg-red-50 text-red-800 rounded-sm p-3 mb-4 text-sm">{error}</div>}

      <TabsContent value="ranking">
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <Label htmlFor="fleet-score-choice">Score</Label>
            <Select value={scoreKey} onValueChange={value => { if (typeof value === 'string' && isScoreKey(value)) setScoreKey(value); }} items={SCORE_KEYS.map(key => ({ value: key, label: key }))}>
              <SelectTrigger id="fleet-score-choice" aria-describedby="fleet-score-help" className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>{SCORE_KEYS.map(key => <SelectItem key={key} value={key}>{key}</SelectItem>)}</SelectContent>
            </Select>
            <span id="fleet-score-help" className="text-xs text-muted-foreground">Choose how findings contribute to each device's score.</span>
            <span id="fleet-category-help" className="text-sm text-gray-600 ml-2">Filter by open finding category</span>
            <ToggleGroup multiple variant="outline" value={categoryFilter} onValueChange={setCategoryFilter} aria-label="Finding categories" aria-describedby="fleet-category-help" className="flex-wrap">
              {allCategories.map(cat => (
                <ToggleGroupItem key={cat} value={cat} className="aria-pressed:bg-primary aria-pressed:text-primary-foreground" title={`Filter the ranking to devices with open ${cat} findings.`}>{cat}</ToggleGroupItem>
              ))}
            </ToggleGroup>
            {categoryFilter.length > 0 && (
              <Button variant="link" size="sm" onClick={() => setCategoryFilter([])}
                title="Drop every category filter and show the whole ranking again.">clear</Button>
            )}
            <div className="flex items-center gap-2">
              <Label htmlFor="fleet-find-clip">Find a Clip</Label>
              <Input id="fleet-find-clip" type="text" value={findClip}
                     onChange={event => setFindClip(event.target.value)}
                     aria-describedby="fleet-find-clip-help" className="w-40" />
              <span id="fleet-find-clip-help" className="text-xs text-muted-foreground">
                Enter a full Clip number or part of a hardware ID to filter the ranking.
              </span>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="text-left border-b-2 border-border text-gray-600">
                  <th className="py-2 pr-2">Clip</th>
                  <th className="py-2 pr-2">Device</th>
                  <th className="py-2 pr-2">Score ({scoreKey})</th>
                  <th className="py-2 pr-2">{trendLabel}</th>
                  <th className="py-2 pr-2">Problems</th>
                  <th className="py-2 pr-2">Evaluated</th>
                </tr>
              </thead>
                {visibleRows.map(row => {
                  const openCats = [...new Set(
                    (row.breakdown || []).filter(b => b.status === 'open').map(b => b.category))];
                  return (
                    <Collapsible key={row.device_id} open={expandedId === row.device_id} onOpenChange={() => openPanel(row.device_id)} render={<tbody />}>
                      <tr className="border-b border-border hover:bg-indigo-50">
                        <td className="py-2 pr-2 font-medium tabular-nums">{row.device_id}</td>
                        <td className="py-2 pr-2 font-medium">
                          <CollapsibleTrigger render={<Button variant="link" size="sm" aria-label={`Clip ${row.device_id}, ${row.device_name}`} title={`Show the score history and findings for Clip ${row.device_id}, ${row.device_name}.`} />}>
                            {row.device_name}
                          </CollapsibleTrigger>
                        </td>
                        <td className="py-2 pr-2 tabular-nums">
                          {(row.scores[scoreKey] ?? 0).toFixed(2)}
                        </td>
                        <td className="py-2 pr-2">{trendFor(row)}</td>
                        <td className="py-2 pr-2">
                          {openCats.map(c => <span key={c} className={chipClass(c)}>{c}</span>)}
                          {row.input_freshness && !row.input_freshness.daily_calcs && (
                            <Badge label="stale data" color="border border-status-neutral-border bg-status-neutral text-status-neutral-foreground"
                                   explain="This device had no processed data for the evaluated date, so data-based checks were skipped. Comms checks still ran." />
                          )}
                        </td>
                        <td className="py-2 pr-2 text-gray-700">{String(row.as_of_date).slice(0, 10)}</td>
                      </tr>
                      <CollapsibleContent render={<tr className="border-b border-border bg-gray-50" />}>
                          <td colSpan={6} className="p-4">
                            {panelLoading && <div className="text-muted-foreground text-sm">Loading…</div>}
                            {panel && (
                              <div>
                                <div className="flex items-center gap-4 mb-3 flex-wrap">
                                  <ScoreSparkline history={panel.score_history} scoreKey={scoreKey} />
                                  <span className="text-xs text-muted-foreground">
                                    90-day score — dashed lines mark scoring-config changes
                                  </span>
                                  <Link className="text-indigo-600 underline text-sm"
                                        to={`/devices/${row.device_id}`}>
                                    Open device workspace
                                  </Link>
                                  {canReadNotes && (
                                    <Link className="text-indigo-600 underline text-sm"
                                          to={`/devices/${row.device_id}/notes`}>
                                      Notes
                                    </Link>
                                  )}
                                </div>
                                {panel.findings.length === 0 && (
                                  <div className="text-sm text-muted-foreground">No findings in the last 30 days.</div>
                                )}
                                {panel.findings.map(f => (
                                  <div key={f.id} className="border border-border rounded-sm bg-white p-3 mb-2">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className={chipClass(f.category)}>{f.problem_type}</span>
                                      <span className="text-sm font-medium">{f.summary}</span>
                                      {f.status === 'resolved' && (
                                        <Badge label={`resolved (${f.resolved_by})`}
                                               color="border border-status-success-border bg-status-success text-status-success-foreground"
                                               explain="This episode is closed. Its score contribution decays with a 7-day half-life from the resolution date." />
                                      )}
                                      {f.suppressed && (
                                        <Badge label="suppressed"
                                               color="border border-status-neutral-border bg-status-neutral text-status-neutral-foreground"
                                               explain="A false-positive label suppresses this finding's score contribution while the detection logic stays unchanged. It will get a fresh hearing if the evaluator's logic or thresholds change." />
                                      )}
                                      {!f.evaluator_enabled && (
                                        <Badge label="evaluator disabled"
                                               color="border border-status-warning-border bg-status-warning text-status-warning-foreground"
                                               explain="This evaluator is currently disabled in the registry. The stored score is as of its run; from the next run this finding contributes zero until the evaluator is re-enabled." />
                                      )}
                                      {f.effective_verdict && (
                                        <span className="text-xs text-muted-foreground">label: {f.effective_verdict}</span>
                                      )}
                                    </div>
                                    <div className="text-xs text-muted-foreground mt-1">
                                      severity {f.severity.toFixed(2)} · confidence {f.confidence.toFixed(2)} ·
                                      first seen {String(f.first_seen).slice(0, 10)} · last seen {String(f.last_seen).slice(0, 10)}
                                    </div>
                                    <Collapsible className="mt-1">
                                      <CollapsibleTrigger render={<Button variant="link" size="sm" title="Show the evidence recorded for this finding." />}>evidence</CollapsibleTrigger>
                                      <CollapsibleContent>
                                        <pre className="text-xs border border-border bg-muted rounded-sm p-2 mt-1 overflow-x-auto">
                                        {JSON.stringify(f.evidence, null, 2)}
                                        </pre>
                                      </CollapsibleContent>
                                    </Collapsible>
                                    {canManage && (
                                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                                        <ToggleGroup variant="outline" value={f.effective_verdict ? [f.effective_verdict] : []}
                                                     onValueChange={values => {
                                                       const verdict = values[0] ?? f.effective_verdict;
                                                       if (verdict) applyLabel(f.id, verdict);
                                                     }}
                                                     aria-label={`Verdict for ${f.summary}`} aria-describedby={`fleet-verdict-help-${f.id}`}>
                                          {VERDICTS.map(v => (
                                            <ToggleGroupItem key={v} value={v} className="aria-pressed:bg-primary aria-pressed:text-primary-foreground" title={`Label this finding ${v.replace('_', ' ')}. The label is saved now and counts from the next run.`}>{v.replace('_', ' ')}</ToggleGroupItem>
                                          ))}
                                        </ToggleGroup>
                                        <Label htmlFor={`finding-note-${f.id}`}>Optional note</Label>
                                        <Input id={`finding-note-${f.id}`} value={labelNote} onChange={e => setLabelNote(e.target.value)} aria-describedby={`fleet-verdict-help-${f.id}`}
                                               placeholder="optional note" className="flex-1 min-w-40" />
                                        <p id={`fleet-verdict-help-${f.id}`} className="text-xs text-muted-foreground">Choose a verdict to save it now with the optional note; it changes the next nightly score.</p>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </td>
                      </CollapsibleContent>
                    </Collapsible>
                  );
                })}
                {!loading && visibleRows.length === 0 && (
                  <tbody><tr><td colSpan={6} className="py-6 text-center text-muted-foreground">
                    {clipQuery || categoryFilter.length
                      ? 'No matching Clips.'
                      : freshness?.latest_published_date
                        ? 'No Clips in the ranking.'
                        : 'No published health scores yet.'}
                  </td></tr></tbody>
                )}
            </table>
          </div>

          {exclusions.length > 0 && (
            <Collapsible className="mt-4" open={showExcluded} onOpenChange={setShowExcluded}>
              <CollapsibleTrigger render={<Button ref={excludedTriggerRef} variant="link" size="md" title="Show or hide devices excluded from every health check." />}>
                {showExcluded ? 'Hide' : 'Show'} {exclusions.length} excluded device{exclusions.length === 1 ? '' : 's'}
              </CollapsibleTrigger>
              <CollapsibleContent>
                <ul className="mt-2 text-sm text-gray-600">
                  {exclusions.map(x => (
                    <li key={x.id} ref={element => {
                      if (element) exclusionRows.current.set(x.id, element);
                      else exclusionRows.current.delete(x.id);
                    }} className="py-1">
                      <span className="font-medium">{x.device_name}</span>
                      {' — '}{x.reason} <span className="text-xs text-gray-600">({x.source})</span>
                      {canManage && (
                        <Button variant="link" size="sm" onClick={() => endExclusion(x.id)}
                                title="End this exclusion. The device is evaluated and ranked again from the next nightly run."
                                className="ml-2">
                          re-include
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              </CollapsibleContent>
            </Collapsible>
          )}
      </TabsContent>

      <TabsContent value="evaluators">
        <div>
          {evalError && <div role="alert" className="border border-status-error-border bg-red-50 text-red-800 rounded-sm p-3 mb-3 text-sm">{evalError}</div>}
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse mb-6">
              <thead>
                <tr className="text-left border-b-2 border-border text-gray-600">
                  <th className="py-2 pr-2">Evaluator</th>
                  <th className="py-2 pr-2">Tier</th>
                  <th className="py-2 pr-2">Version</th>
                  <th className="py-2 pr-2">Enabled</th>
                  <th className="py-2 pr-2">Weight</th>
                  <th className="py-2 pr-2">Last run</th>
                  {canManage && <th className="py-2 pr-2">Edit</th>}
                </tr>
              </thead>
              <tbody>
                {evaluators.map(ev => {
                  const edit = evalEdit[ev.evaluator_key];
                  const errorFor = (field: EvaluatorField) =>
                    evalFieldError?.key === ev.evaluator_key && evalFieldError.field === field
                      ? evalFieldError.message
                      : null;
                  return (
                    <tr key={ev.evaluator_key} className="border-b border-border align-top">
                      <td className="py-2 pr-2 max-w-md">
                        <div className="font-medium">{ev.evaluator_key}</div>
                        <div className="text-xs text-muted-foreground">{ev.problem_types.join(', ')}</div>
                        {ev.description && (
                          <div className="text-xs text-gray-600 mt-1">{ev.description}</div>
                        )}
                        <Collapsible className="mt-1">
                          <CollapsibleTrigger render={<Button variant="link" size="sm" title="Explain this evaluator's current configuration and resolution rule." />}>
                            current settings explained
                          </CollapsibleTrigger>
                          <CollapsibleContent><dl className="text-xs mt-1">
                            {Object.entries(ev.config).map(([param, value]) => {
                              const doc = EVALUATOR_PARAM_DOCS[ev.evaluator_key]?.[param];
                              return (
                                <div key={param} className="mb-1">
                                  <dt className="font-medium text-gray-700">
                                    {doc?.label ?? param} = {String(value)}{doc?.unit ? ` ${doc.unit}` : ''}
                                  </dt>
                                  {doc && <dd className="text-muted-foreground ml-3">{doc.explain}</dd>}
                                </div>
                              );
                            })}
                            <div className="mb-1">
                              <dt className="font-medium text-gray-700">
                                resolve after misses = {ev.resolve_after_misses}
                              </dt>
                              <dd className="text-muted-foreground ml-3">
                                Consecutive clean evaluations before an open finding
                                auto-resolves. Skipped or errored days never count.
                              </dd>
                            </div>
                          </dl></CollapsibleContent>
                        </Collapsible>
                      </td>
                      <td className="py-2 pr-2">{ev.tier}</td>
                      <td className="py-2 pr-2">{ev.version}</td>
                      <td className="py-2 pr-2">
                        {canManage ? (
                          <div className="flex items-center gap-2">
                            <Switch checked={ev.enabled} onCheckedChange={() => toggleEvaluator(ev)} aria-label={`Enable ${ev.evaluator_key}`} aria-describedby={`evaluator-switch-help-${ev.evaluator_key}`} title={ev.enabled ? 'Turn this evaluator off from the next nightly run.' : 'Turn this evaluator on from the next nightly run.'} />
                            <span>{ev.enabled ? 'enabled' : 'disabled'}</span>
                            <span id={`evaluator-switch-help-${ev.evaluator_key}`} className="sr-only">Changes this evaluator's participation in the next nightly health run.</span>
                          </div>
                        ) : (ev.enabled ? 'yes' : 'no')}
                      </td>
                      <td className="py-2 pr-2">{ev.weight}</td>
                      <td className="py-2 pr-2 text-xs text-gray-600">
                        {ev.last_run
                          ? `${ev.last_run.ok} ok · ${ev.last_run.errors} err · ${ev.last_run.skipped} skipped · ${ev.last_run.findings_emitted ?? 0} findings`
                          : '—'}
                      </td>
                      {canManage && (
                        <td className="py-2 pr-2">
                          {edit ? (
                            <div className="flex flex-col gap-1">
                              <Label htmlFor={`evaluator-weight-${ev.evaluator_key}`}>Weight</Label>
                              <Input id={`evaluator-weight-${ev.evaluator_key}`} value={edit.weight} placeholder={`weight (${ev.weight})`} aria-describedby={`evaluator-weight-help-${ev.evaluator_key}${errorFor('weight') ? ` evaluator-weight-error-${ev.evaluator_key}` : ''}`} aria-invalid={!!errorFor('weight') || undefined}
                                     onChange={e => updateEvaluatorEdit(ev.evaluator_key, { ...edit, weight: e.target.value })}
                                     className="text-xs" />
                              <p id={`evaluator-weight-help-${ev.evaluator_key}`} className="text-xs text-muted-foreground">Changes how much this evaluator contributes to the next score.</p>
                              {errorFor('weight') && <p id={`evaluator-weight-error-${ev.evaluator_key}`} role="alert" className="text-xs text-red-700">{errorFor('weight')}</p>}
                              <Label htmlFor={`evaluator-misses-${ev.evaluator_key}`}>Resolve after misses</Label>
                              <Input id={`evaluator-misses-${ev.evaluator_key}`} value={edit.resolve_after_misses} placeholder={`resolve after (${ev.resolve_after_misses})`} aria-describedby={`evaluator-misses-help-${ev.evaluator_key}${errorFor('resolve_after_misses') ? ` evaluator-misses-error-${ev.evaluator_key}` : ''}`} aria-invalid={!!errorFor('resolve_after_misses') || undefined}
                                     onChange={e => updateEvaluatorEdit(ev.evaluator_key, { ...edit, resolve_after_misses: e.target.value })}
                                     className="text-xs" />
                              <p id={`evaluator-misses-help-${ev.evaluator_key}`} className="text-xs text-muted-foreground">Clean runs required before an open finding resolves.</p>
                              {errorFor('resolve_after_misses') && <p id={`evaluator-misses-error-${ev.evaluator_key}`} role="alert" className="text-xs text-red-700">{errorFor('resolve_after_misses')}</p>}
                              <Label htmlFor={`evaluator-config-${ev.evaluator_key}`}>Settings JSON</Label>
                              <Textarea id={`evaluator-config-${ev.evaluator_key}`} value={edit.config} rows={3} aria-describedby={`evaluator-config-help-${ev.evaluator_key}${errorFor('config') ? ` evaluator-config-error-${ev.evaluator_key}` : ''}`} aria-invalid={!!errorFor('config') || undefined}
                                        onChange={e => updateEvaluatorEdit(ev.evaluator_key, { ...edit, config: e.target.value })}
                                        className="text-xs font-mono" />
                              <p id={`evaluator-config-help-${ev.evaluator_key}`} className="text-xs text-muted-foreground">JSON thresholds used by this evaluator on the next nightly run.</p>
                              {errorFor('config') && <p id={`evaluator-config-error-${ev.evaluator_key}`} role="alert" className="text-xs text-red-700">{errorFor('config')}</p>}
                              <div className="flex gap-2">
                                <Button variant="primary" size="sm" onClick={() => saveEvaluator(ev)}
                                        disabled={!evaluatorHasChanges(ev, edit)}
                                        aria-describedby={!evaluatorHasChanges(ev, edit) ? `evaluator-save-reason-${ev.evaluator_key}` : undefined}
                                        title="Save the weight, resolve-after count and settings typed above. They take effect on the next nightly run.">save</Button>
                                <Button variant="secondary" size="sm" onClick={() => {
                                  setEvalEdit(p => ({ ...p, [ev.evaluator_key]: undefined }));
                                  setEvalFieldError(null);
                                }}
                                        title="Close the editor without saving. The evaluator keeps its current settings.">cancel</Button>
                              </div>
                              {!evaluatorHasChanges(ev, edit) && <p id={`evaluator-save-reason-${ev.evaluator_key}`} className="text-xs text-muted-foreground">No changes to save.</p>}
                            </div>
                          ) : (
                            <Button variant="secondary" size="sm" onClick={() => setEvalEdit(p => ({
                                ...p,
                                [ev.evaluator_key]: {
                                  weight: '', resolve_after_misses: '',
                                  config: JSON.stringify(ev.config, null, 2),
                                },
                              }))}
                              title="Change this evaluator's weight, how many clean runs resolve a finding, and its settings.">edit</Button>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {canManage && (
            <div className="border border-border rounded-sm p-3 max-w-lg">
              <h2 className="text-sm font-semibold mb-2">Add exclusion</h2>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="min-w-0 space-y-1">
                  <Label htmlFor="exclusion-device-id">Device ID</Label>
                  <Input id="exclusion-device-id" value={newExclusion.device_id} placeholder="device id" aria-describedby="exclusion-device-help"
                         onChange={e => setNewExclusion(p => ({ ...p, device_id: e.target.value }))}
                         className="w-full" />
                  <p id="exclusion-device-help" className="text-xs text-muted-foreground">Numeric ID of the device to skip.</p>
                </div>
                <div className="min-w-0 space-y-1">
                  <Label htmlFor="exclusion-reason">Reason</Label>
                  <Input id="exclusion-reason" value={newExclusion.reason} placeholder="reason" aria-describedby="exclusion-reason-help"
                         onChange={e => setNewExclusion(p => ({ ...p, reason: e.target.value }))}
                         className="w-full" />
                  <p id="exclusion-reason-help" className="text-xs text-muted-foreground">Explain why this device should not affect the ranking.</p>
                </div>
              </div>
              <Button variant="primary" onClick={addExclusion}
                      disabled={!newExclusion.device_id || !newExclusion.reason}
                      aria-describedby={!newExclusion.device_id || !newExclusion.reason ? 'exclusion-disabled-reason' : undefined}
                      title="Leave this device out of every health check and the ranking from the next run, with the reason typed here.">
                Exclude
              </Button>
              {(!newExclusion.device_id || !newExclusion.reason) && <p id="exclusion-disabled-reason" className="text-xs text-muted-foreground">Enter a device ID and reason to exclude it.</p>}
              <p className="text-xs text-muted-foreground mt-1">
                Excluded devices are skipped by every evaluator and drop out of the ranking
                from the next run.
              </p>
            </div>
          )}
        </div>
      </TabsContent>
    </Tabs>
  );
};

export default FleetHealthPage;
